import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, InputAdornment, TextField } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { getPumpColumnFormSchema, PumpColumnForm as PumpColumnFormType } from "../../../types/forms/capture";
import { PumpColumn } from "../../../types/model/pumpColumn";

interface PumpColumnFormProps {
  pumpColumnData?: PumpColumn;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: PumpColumnFormType) => Promise<void>;
}

export default function PumpColumnForm({
  pumpColumnData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<PumpColumnFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getPumpColumnFormSchema(t);
  const methods = useForm<PumpColumnFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      waterLevelGuardDepth: pumpColumnData?.waterLevelGuardDepth ?? null,
      speedControl: pumpColumnData?.speedControl ?? null,
      piezometricTubeType: pumpColumnData?.piezometricTubeType ?? null,
      piezometricTubeDiameter: pumpColumnData?.piezometricTubeDiameter ?? null,
      piezometricTubeDepth: pumpColumnData?.piezometricTubeDepth ?? null
    },
  });

  const handleFormSubmit = async (values: PumpColumnFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={t("captures.capture.pump-column.water-level-guard-depth")}                         
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              value={
                methods.watch("waterLevelGuardDepth") ??
                ""
              }
              onChange={(e) => {
                const value = e.target.value;
                methods.setValue(
                  "waterLevelGuardDepth",
                  value === "" ? null : Number(value),
                  { shouldDirty: true }
                );
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.waterLevelGuardDepth}
              helperText={methods.formState.errors.waterLevelGuardDepth?.message}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={t("captures.capture.pump-column.speed-control")}                         
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register(
                "speedControl"
              )}
              error={!!methods.formState.errors.speedControl}
              helperText={methods.formState.errors.speedControl?.message}
            />
          </Grid2>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={t("captures.capture.pump-column.piezometric-tube-type")}                         
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register(
                "piezometricTubeType"
              )}
              error={!!methods.formState.errors.piezometricTubeType}
              helperText={methods.formState.errors.piezometricTubeType?.message}
            />
          </Grid2>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={t("captures.capture.pump-column.piezometric-tube-diameter")}                         
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              value={
                methods.watch("piezometricTubeDiameter") ??
                ""
              }
              onChange={(e) => {
                const value = e.target.value;
                methods.setValue(
                  "piezometricTubeDiameter",
                  value === "" ? null : Number(value),
                  { shouldDirty: true }
                );
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.mm")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.piezometricTubeDiameter}
              helperText={methods.formState.errors.piezometricTubeDiameter?.message}
            />
          </Grid2>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={t("captures.capture.pump-column.piezometric-tube-depth")}                         
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              value={
                methods.watch("piezometricTubeDepth") ??
                ""
              }
              onChange={(e) => {
                const value = e.target.value;
                methods.setValue(
                  "piezometricTubeDepth",
                  value === "" ? null : Number(value),
                  { shouldDirty: true }
                );
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.piezometricTubeDepth}
              helperText={methods.formState.errors.piezometricTubeDepth?.message}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}